import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import {
  DESKTOP_MEDIA_QUERY,
  REDUCED_MOTION_QUERY,
  getLandingHeroSources,
  isDesktopViewport,
  nextLandingHeroVideo,
  pickLandingHeroVideo,
  prefersReducedMotion,
  rememberLandingHeroVideo,
  subscribeToMediaQuery,
} from '../../../../util/heroVideo';

import css from './BackgroundLoopVideo.module.css';

/**
 * Full-bleed muted hero video. Picks a random clip on load, then plays the next
 * clip when the current one ends. Renders nothing on the server, when motion is
 * reduced, or when the file fails to load.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @param {function(): void} props.onFallback called when the CMS photo should show
 * @returns {JSX.Element | null}
 */
export const BackgroundLoopVideo = props => {
  const { className, rootClassName, onFallback } = props;
  const [current, setCurrent] = useState(null);
  const [next, setNext] = useState(null);
  const [isDesktop, setIsDesktop] = useState(true);
  const [currentReady, setCurrentReady] = useState(false);
  const [nextReady, setNextReady] = useState(false);
  const [hasError, setHasError] = useState(false);
  const advanceWhenReadyRef = useRef(false);
  const currentRef = useRef(null);
  const nextRef = useRef(null);
  const currentVideoRef = useRef(null);
  const nextVideoRef = useRef(null);

  currentRef.current = current;
  nextRef.current = next;

  const promoteNext = () => {
    const incoming = nextRef.current;
    if (!incoming) {
      return;
    }

    rememberLandingHeroVideo(incoming);
    setCurrent(incoming);
    setNext(nextLandingHeroVideo(incoming));
    setCurrentReady(true);
    setNextReady(false);
    advanceWhenReadyRef.current = false;
  };

  const replayCurrent = () => {
    const node = currentVideoRef.current;
    if (!node) {
      return;
    }
    node.currentTime = 0;
    const playPromise = node.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  };

  useEffect(() => {
    if (prefersReducedMotion()) {
      if (typeof onFallback === 'function') {
        onFallback();
      }
      return undefined;
    }

    const first = pickLandingHeroVideo();
    setCurrent(first);
    setIsDesktop(isDesktopViewport());

    const unsubscribeDesktop = subscribeToMediaQuery(DESKTOP_MEDIA_QUERY, matches => {
      setIsDesktop(matches);
      setCurrentReady(false);
      setNextReady(false);
    });
    const unsubscribeMotion = subscribeToMediaQuery(REDUCED_MOTION_QUERY, matches => {
      if (matches) {
        setCurrent(null);
        setNext(null);
        setCurrentReady(false);
        setNextReady(false);
        if (typeof onFallback === 'function') {
          onFallback();
        }
        return;
      }
      setCurrent(currentChar => {
        if (currentChar) {
          return currentChar;
        }
        return pickLandingHeroVideo();
      });
    });

    return () => {
      unsubscribeDesktop();
      unsubscribeMotion();
    };
  }, []);

  const currentSources = current ? getLandingHeroSources(current, isDesktop) : null;
  const nextSources = next ? getLandingHeroSources(next, isDesktop) : null;

  useEffect(() => {
    if (!currentReady || !current) {
      return;
    }
    setNext(existing => existing || nextLandingHeroVideo(current));
  }, [current, currentReady]);

  useEffect(() => {
    const node = currentVideoRef.current;
    if (!node || !currentReady) {
      return;
    }
    if (node.paused || node.ended) {
      node.currentTime = 0;
    }
    const playPromise = node.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }, [current, currentReady, currentSources?.mp4]);

  if (!current || !currentSources || hasError) {
    return null;
  }

  const classes = classNames(rootClassName || css.video, className);

  return (
    <>
      <video
        key={currentSources.mp4}
        ref={currentVideoRef}
        className={classes}
        autoPlay
        muted
        playsInline
        preload="auto"
        poster={currentSources.poster}
        aria-hidden="true"
        data-hero-video={current}
        onCanPlay={() => setCurrentReady(true)}
        onEnded={() => {
          if (nextReady) {
            promoteNext();
            return;
          }
          if (nextRef.current) {
            advanceWhenReadyRef.current = true;
            const preload = nextVideoRef.current;
            if (preload) {
              const playPromise = preload.play();
              if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(() => {});
              }
            }
            return;
          }
          replayCurrent();
        }}
        onError={() => {
          setHasError(true);
          if (typeof onFallback === 'function') {
            onFallback();
          }
        }}
        src={currentSources.mp4}
      />
      {nextSources ? (
        <video
          key={nextSources.mp4}
          ref={nextVideoRef}
          className={classNames(classes, css.preload)}
          muted
          playsInline
          preload="auto"
          aria-hidden="true"
          data-hero-video-preload={next}
          onCanPlay={() => {
            setNextReady(true);
            if (advanceWhenReadyRef.current) {
              promoteNext();
            }
          }}
          onError={() => {
            const skip = nextLandingHeroVideo(nextRef.current);
            if (!skip || skip === currentRef.current) {
              setNext(null);
              setNextReady(false);
              if (advanceWhenReadyRef.current) {
                advanceWhenReadyRef.current = false;
                replayCurrent();
              }
              return;
            }
            setNext(skip);
            setNextReady(false);
          }}
          src={nextSources.mp4}
        />
      ) : null}
    </>
  );
};

BackgroundLoopVideo.displayName = 'BackgroundLoopVideo';
